import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { DiseaseListSortOptionValues } from 'models/SortByOptions';
import { nameof } from 'utils/typeHelpers';

export type DiseaseActivitiesInLocationState = {
  isLoading: boolean;
  hasError: boolean;
  allDiseaseIds: number[];
  diseaseInformations: Record<number, dto.DiseaseInformationModel>;
  importationRisks: Record<number, dto.RiskModel>;
  outbreakPotentialCategories: Record<number, dto.OutbreakPotentialCategoryModel>;
  caseCounts: Record<number, dto.CaseCountModel>;
  nestedCaseCounts: Record<number, dto.HierarchicalCaseCountModel>;
  sortBy: DiseaseListSortOptionValues;
  lastUpdatedEventDates: Record<number, string>;
  searchString: string;
};

export const InitialDiseaseActivitiesInLocationState: DiseaseActivitiesInLocationState = {
  isLoading: false,
  hasError: false,
  allDiseaseIds: [],
  diseaseInformations: {},
  importationRisks: {},
  outbreakPotentialCategories: {},
  caseCounts: {},
  nestedCaseCounts: {},
  sortBy: 'predicted-cases-of',
  lastUpdatedEventDates: {},
  searchString: '',
};

const diseaseActivitiesInLocationSlice = createSlice({
  name: nameof<InsightsState>('diseaseActivitiesInLocation'),
  initialState: InitialDiseaseActivitiesInLocationState,
  reducers: {
    loadAllDiseaseRisksStart: state => {
      state.isLoading = true;
    },
    loadAllDiseaseRisksSuccess: (state, action: PayloadAction<dto.DiseaseRiskModel[]>) => {
      const risks = action.payload;
      state.isLoading = false;
      state.hasError = false;
      state.diseaseInformations = {};
      state.importationRisks = {};
      state.outbreakPotentialCategories = {};
      risks.forEach(risk => {
        const diseaseId: number = risk.diseaseInformation.id;
        state.diseaseInformations[diseaseId] = risk.diseaseInformation;
        state.importationRisks[diseaseId] = risk.importationRisk;
        state.outbreakPotentialCategories[diseaseId] = risk.outbreakPotentialCategory;
        state.caseCounts[diseaseId] = risk.caseCounts;
        state.lastUpdatedEventDates[diseaseId] = risk.lastUpdatedEventDate;
      });
      state.allDiseaseIds = risks.map(disease => disease.diseaseInformation.id);
    },
    loadAllDiseaseRisksError: state => {
      state.isLoading = false;
      state.hasError = true;
    },
    clearNestedCaseCounts: state => {
      state.nestedCaseCounts = {};
    },
    loadNestedCaseCountsSuccess: (
      state,
      action: PayloadAction<{ nestedCaseCounts: dto.HierarchicalCaseCountModel; diseaseId: number }>
    ) => {
      state.nestedCaseCounts[action.payload.diseaseId] = action.payload.nestedCaseCounts;
    },
    sortDiseaseRisks: (state, action: PayloadAction<DiseaseListSortOptionValues>) => {
      state.sortBy = action.payload;
    },
    searchDiseases: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload;
    },
    resetFilters: state => {
      state.sortBy = InitialDiseaseActivitiesInLocationState.sortBy;
      state.searchString = InitialDiseaseActivitiesInLocationState.searchString;
    },
  },
});

const { actions, reducer } = diseaseActivitiesInLocationSlice;
export const {
  loadAllDiseaseRisksStart,
  loadAllDiseaseRisksSuccess,
  loadAllDiseaseRisksError,
  clearNestedCaseCounts,
  loadNestedCaseCountsSuccess,
  sortDiseaseRisks,
  searchDiseases,
  resetFilters,
} = actions;
export default reducer;
